import { useCallback, useMemo, useRef, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { providerDisplayName } from "@pontimed/types";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { cn } from "@pontimed/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Building2,
  Calendar as CalendarIcon,
  CheckCircle2,
  ExternalLink,
  FileText,
  Globe,
  GraduationCap,
  Home,
  IdCard,
  Images,
  Loader2,
  ShieldCheck,
  Stethoscope,
  Trash2,
  User as UserIcon,
  Video,
} from "lucide-react";

import { AccountSecurityBanner } from "@/components/account/account-security-banner";
import {
  CompletenessChecklist,
  CompletenessRing,
  type CompletenessMissing,
  type CompletenessTarget,
} from "@/components/account/provider/profile/completeness-ring";
import { CredentialsEditor } from "@/components/account/provider/profile/credentials-editor";
import { FacilityLinker } from "@/components/account/provider/profile/facility-linker";
import {
  FacilitySettingsCard,
  type FacilityData,
  type OtherFacility,
} from "@/components/account/provider/profile/facility-settings-card";
import { PhotoGalleryEditor } from "@/components/account/provider/profile/photo-gallery-editor";
import { ProviderBioEditor } from "@/components/account/provider/profile/provider-bio-editor";
import { ServiceAreasEditor } from "@/components/account/provider/profile/service-areas-editor";
import { UnifiedCalendarTab } from "@/components/account/provider/profile/unified-calendar-tab";
import {
  ServicesTariffsEditor,
  type AvailabilityAreaOption,
  type AvailabilityFacilityOption,
  type PaymentMethod,
  type ProviderServiceRow,
} from "@/components/account/provider/profile/services-tariffs-editor";
import {
  SpecialtyPicker,
  type ProviderSpecialtyEntry as PickerSpecialtyEntry,
} from "@/components/account/provider/profile/specialty-picker";
import { VerificationDocuments } from "@/components/account/provider/profile/verification-documents";
import { VideoConsultationEditor } from "@/components/account/provider/profile/video-consultation-editor";
import { ProviderIdentityEditor } from "@/components/provider/provider-identity-editor";
import { PAGE_TITLE_CLASS } from "@/components/layout/page-header";
import { TwoFactorChallengeNotice } from "@/components/security/two-factor-challenge-notice";
import { Button } from "@/components/ui/button";
import {
  type EmailEntry,
  type PhoneEntry,
  ProviderConsultationModesEditor,
  ProviderLanguagesEditor,
  ProviderProfessionEditor,
} from "@/components/provider/provider-profile-extras";
import { CollapsibleSection } from "@/components/ui/collapsible-section";
import { LoadErrorState } from "@/components/ui/load-error-state";
import { ResponsiveSheet } from "@/components/ui/responsive-sheet";
import { Skeleton } from "@/components/ui/skeleton";
import {
  SwipeableTabsContent,
  type SwipeableTabsHandle,
} from "@/components/ui/swipeable-tabs-content";
import { TabBar, type TabItem } from "@/components/ui/tab-bar";
import { regionName } from "@/lib/region-name";
import { showToast } from "@/lib/ui/toast";
import { tLocalized, type LocalizedString } from "@/types/localized-string";
import { buildWebUrl } from "@/lib/web-url";

interface SpecialtyRef {
  id: string;
  code: string;
  name: LocalizedString;
}

interface ProviderSpecialtyEntry {
  specialtyId: string;
  isPrimary: boolean;
  approvalStatus: "pending" | "approved" | "rejected";
  rejectionReason?: string | null;
  specialty: SpecialtyRef;
}

/**
 * Shape returned by `/api/account/provider/profile` for each linked facility.
 * Extends `FacilityData` (consumed by `<FacilitySettingsCard>`) with a few
 * extra fields used by the read-only `FacilityRow` summary.
 */
type FacilityLink = FacilityData & {
  status: string;
  isActive: boolean;
};

interface ProviderData {
  id: string;
  firstName: string;
  lastName: string;
  gender: string | null;
  grade: string | null;
  licenseNo: string | null;
  university: string | null;
  graduationYear: number | null;
  yearsExperience: number | null;
  profession: string;
  isVerified: boolean;
  profileStatus: string;
  bio: LocalizedString | null;
  photoUrl: string | null;
  slugRo: string | null;
  slugEn: string | null;
  acceptsNewPatients: boolean;
  offersVideoConsultation: boolean;
  acceptsInOffice: boolean;
  acceptsHomeVisits: boolean;
  acceptsPhoneConsultation: boolean;
  phones: PhoneEntry[];
  emails: EmailEntry[];
  showPhone: boolean;
  showEmail: boolean;
  bookingMethod: string;
  bookingPhone: string | null;
  bookingEmail: string | null;
  paymentMethods: PaymentMethod[];
  requiresPrepayment: boolean | null;
  insuranceProviders?: { insuranceId: string }[];
  languages: string[];
  facilityProviders: FacilityLink[];
  providerSpecialties: ProviderSpecialtyEntry[];
  countryCode: string;
  profileCompleted: boolean;
}

interface ProfileResponse {
  provider: ProviderData;
  serviceRows?: ProviderServiceRow[];
  availableLanguages?: string[];
  /** Server-computed (same helper as the menu badge — P22). */
  completeness?: { percent: number; missing: CompletenessMissing[] };
}

type ProfileTab = "profile" | "locations" | "services" | "calendar" | "verification";
const VALID_TABS: readonly ProfileTab[] = [
  "profile",
  "locations",
  "services",
  "calendar",
  "verification",
] as const;

interface ProfileSearch {
  tab: ProfileTab;
}

const PROFILE_KEY = ["api", "/api/account/provider/profile"] as const;

const profileQueryOptions = {
  queryKey: PROFILE_KEY,
  queryFn: () => apiFetch.get<ProfileResponse>("/api/account/provider/profile"),
  retry: (failureCount: number, error: unknown) => {
    if (error instanceof ApiError && (error.status === 401 || error.status === 403)) return false;
    return failureCount < 2;
  },
} as const;

interface SpecialtyCatalogRow {
  id: string;
  code: string;
  name: LocalizedString;
  type?: string;
  parentSpecialtyId?: string | null;
}

export const Route = createFileRoute("/_authenticated/account/provider/profile")({
  validateSearch: (search: Record<string, unknown>): ProfileSearch => {
    const rawTab = typeof search.tab === "string" ? search.tab : "";
    return {
      tab: VALID_TABS.includes(rawTab as ProfileTab) ? (rawTab as ProfileTab) : "profile",
    };
  },
  // Prefetch the provider profile in parallel with the route chunk download.
  // The specialty catalog + service-areas queries chain off `data.countryCode`
  // and `!!data` respectively, so they still wait — but the primary roundtrip
  // is no longer blocking component mount.
  loader: ({ context }) => context.queryClient.ensureQueryData(profileQueryOptions),
  component: ProviderProfilePage,
});

type SectionKey =
  | "basic"
  | "bio"
  | "gallery"
  | "profession"
  | "specialty"
  | "languages"
  | "credentials"
  | "consultationTypes"
  | "facilitySettings"
  | "video"
  | "serviceAreas";

function ProviderProfilePage() {
  const { t, i18n } = useLingui();
  const locale = i18n.locale;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { tab: activeTab } = Route.useSearch();
  const swipeRef = useRef<SwipeableTabsHandle>(null);
  // Accordion: only one section open at a time across tabs (matches legacy).
  const [openSection, setOpenSection] = useState<SectionKey | null>("basic");
  const toggleSection = useCallback((key: SectionKey) => {
    setOpenSection((prev) => (prev === key ? null : key));
  }, []);
  // Facility unlink confirm (ResponsiveSheet) — legacy used window.confirm.
  const [confirmRemove, setConfirmRemove] = useState<{ facilityId: string; name: string } | null>(
    null,
  );
  const [removing, setRemoving] = useState(false);
  // Verification tab completion % (fed by <VerificationDocuments>).
  const [verificationCompletion, setVerificationCompletion] = useState(0);

  // DELETE marks the FacilityProvider row status="declined" server-side, so
  // it drops out of both active and pending lists. Same endpoint the
  // affiliations page uses for cancel (see affiliations-client.tsx).
  const removeFacility = useCallback(
    async (facilityId: string) => {
      setRemoving(true);
      const toast = showToast(t`Saving…`, "loading");
      try {
        await apiFetch.raw(
          `/api/account/provider/facilities?facilityId=${encodeURIComponent(facilityId)}`,
          { method: "DELETE" },
        );
        await queryClient.invalidateQueries({ queryKey: PROFILE_KEY });
        window.dispatchEvent(new Event("account-state-changed"));
        toast.update(t`Saved`, "success");
      } catch {
        toast.update(t`Could not save. Try again.`, "error");
      } finally {
        setRemoving(false);
      }
    },
    [queryClient, t],
  );

  const setTab = useCallback(
    (next: ProfileTab) => {
      navigate({ to: "/account/provider/profile", search: { tab: next } });
    },
    [navigate],
  );

  const query = useQuery(profileQueryOptions);

  // Basic-information editor seed. Memoised on the cached provider (a new
  // object only when the cache changes): the editor re-syncs its local state
  // whenever this object changes, so a fresh literal per render would keep
  // resetting it.
  const cachedProvider = query.data?.provider;
  const identityInitial = useMemo(
    () =>
      cachedProvider
        ? {
            id: cachedProvider.id,
            firstName: cachedProvider.firstName,
            lastName: cachedProvider.lastName,
            gender: cachedProvider.gender,
            grade: cachedProvider.grade,
            profession: cachedProvider.profession,
            licenseNo: cachedProvider.licenseNo,
            university: cachedProvider.university,
            graduationYear: cachedProvider.graduationYear,
            yearsExperience: cachedProvider.yearsExperience,
            bio: cachedProvider.bio,
            photoUrl: cachedProvider.photoUrl,
          }
        : null,
    [cachedProvider],
  );

  const countryCode = query.data?.provider.countryCode;
  const catalogQuery = useQuery({
    queryKey: ["api", "/api/specialties", countryCode] as const,
    queryFn: () =>
      apiFetch.get<SpecialtyCatalogRow[]>(
        countryCode
          ? `/api/specialties?country=${encodeURIComponent(countryCode)}&limit=500`
          : "/api/specialties?limit=500",
      ),
    enabled: !!countryCode,
  });

  // Service-area options for the ServicesTariffsEditor availability picker.
  // Lazy-fetched: the picker only references them, so OK to load alongside.
  const serviceAreasQuery = useQuery({
    queryKey: ["api", "/api/account/provider/service-areas"] as const,
    queryFn: () =>
      apiFetch.get<{ areas: { id: string; name: string | null; cityName: string }[] }>(
        "/api/account/provider/service-areas",
      ),
    enabled: !!query.data,
  });

  if (query.isLoading) return <ProfileSkeleton />;
  if (query.isError) {
    if (query.error instanceof ApiError && query.error.status === 403) {
      const body = query.error.body as { error?: string } | null;
      if (body?.error === "TOTP_CHALLENGE_REQUIRED")
        return <TwoFactorChallengeNotice className="mt-2" />;
    }
    return <ProfileErrorState onRetry={() => query.refetch()} />;
  }

  const provider = query.data!.provider;
  const availableLanguages = query.data!.availableLanguages ?? ["ro", "en"];
  const serviceRows = query.data!.serviceRows ?? [];
  const fullName = `${provider.firstName} ${provider.lastName}`.trim();
  const primarySpecialty = provider.providerSpecialties.find((ps) => ps.isPrimary);
  const activeFacilities = provider.facilityProviders.filter((fp) => fp.status === "active");
  const pendingFacilities = provider.facilityProviders.filter((fp) => fp.status === "pending");
  // Public profile lives on the marketing site (no SPA route) — absolute URL
  // on WEB_URL (pontimed.app in prod, pontimed.local in dev).
  // Country = the provider's own country (their profile is published there).
  const publicSlug = provider.slugRo || provider.slugEn || provider.id;
  const publicHref = buildWebUrl(
    `provider/${publicSlug}`,
    locale,
    (provider.countryCode || "ro").toLowerCase(),
  );

  // Completeness comes from the server — the same helper feeds the menu
  // badge, so the two never disagree (P22). Older API builds don't send it:
  // then the ring is simply not shown rather than computed differently here.
  const completeness = query.data!.completeness ?? null;
  const openCompletenessTarget = (target: CompletenessTarget) => {
    if (target.section) setOpenSection(target.section);
    setTab(target.tab);
  };

  const tabs: readonly TabItem<ProfileTab>[] = [
    { id: "profile", label: <Trans>Profile</Trans>, icon: <UserIcon className="size-4" /> },
    { id: "locations", label: <Trans>Locations</Trans>, icon: <Building2 className="size-4" /> },
    { id: "services", label: <Trans>Services</Trans>, icon: <Stethoscope className="size-4" /> },
    {
      id: "calendar",
      label: <Trans>Appointment slots</Trans>,
      icon: <CalendarIcon className="size-4" />,
    },
    {
      id: "verification",
      label: <Trans>Verification</Trans>,
      icon: <ShieldCheck className="size-4" />,
    },
  ];

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Page header: name (shared h1 style) + status/specialty pills +
          view-public-profile link, completeness ring right-aligned, and the
          checklist of what is missing underneath. */}
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <h1 className={PAGE_TITLE_CLASS}>
            {providerDisplayName(fullName, { profession: provider.profession, locale })}
          </h1>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            {provider.isVerified && (
              <span className="bg-success-soft text-success-strong inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.625rem] font-semibold">
                <CheckCircle2 className="size-3" />
                <Trans>Verified</Trans>
              </span>
            )}
            <ProfileStatusPill status={provider.profileStatus} />
            {primarySpecialty && (
              <span className="bg-muted text-muted-foreground inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.625rem] font-medium">
                <Stethoscope className="size-3" />
                {tLocalized(primarySpecialty.specialty.name, locale)}
                {primarySpecialty.approvalStatus !== "approved" && (
                  <span className="bg-warning-soft text-warning-strong ml-1 rounded-full px-1.5 py-0.5 text-[0.5625rem] font-medium">
                    {primarySpecialty.approvalStatus === "pending" ? (
                      <Trans>Pending review</Trans>
                    ) : (
                      <Trans>Rejected</Trans>
                    )}
                  </span>
                )}
              </span>
            )}
            <a
              href={publicHref}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:text-primary-hover inline-flex min-h-11 items-center gap-1.5 text-sm font-medium"
            >
              <ExternalLink className="size-3.5" aria-hidden />
              <Trans>View public profile</Trans>
            </a>
          </div>
        </div>
        {completeness ? (
          <div className="shrink-0">
            <CompletenessRing percent={completeness.percent} />
          </div>
        ) : null}
      </div>

      {completeness && completeness.missing.length > 0 ? (
        <div className="mt-4">
          <CompletenessChecklist missing={completeness.missing} onSelect={openCompletenessTarget} />
        </div>
      ) : null}

      <TabBar
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setTab}
        onActiveTabClick={() => swipeRef.current?.scrollActiveToTop()}
        className="mt-6"
      />

      <SwipeableTabsContent
        ref={swipeRef}
        activeTab={activeTab}
        tabIds={VALID_TABS}
        onTabChange={setTab}
        className="mt-4"
        renderPanel={(id) => {
          if (id === "profile") {
            return (
              <div className="space-y-3">
                <CollapsibleSection
                  icon={<UserIcon className="size-4" />}
                  title={<Trans>Basic information</Trans>}
                  open={openSection === "basic"}
                  onToggle={() => toggleSection("basic")}
                >
                  {identityInitial ? (
                    <ProviderIdentityEditor initial={identityInitial} invalidateKey={PROFILE_KEY} />
                  ) : null}
                </CollapsibleSection>

                {/* Section order mirrors legacy provider-profile-editor.tsx:
                    Basic → Photo gallery → Provider type → Specialty →
                    Biography → Professional background → Languages spoken. */}
                <CollapsibleSection
                  icon={<Images className="size-4" />}
                  title={<Trans>Photo gallery</Trans>}
                  open={openSection === "gallery"}
                  onToggle={() => toggleSection("gallery")}
                >
                  <PhotoGalleryEditor />
                </CollapsibleSection>

                <CollapsibleSection
                  icon={<IdCard className="size-4" />}
                  title={<Trans>Provider type</Trans>}
                  open={openSection === "profession"}
                  onToggle={() => toggleSection("profession")}
                >
                  <ProviderProfessionEditor
                    initial={
                      provider.profession as
                        | "md_doctor"
                        | "dentist"
                        | "psychologist"
                        | "physiotherapist"
                        | "nutritionist"
                        | "midwife"
                        | "occupational_therapist"
                        | "speech_therapist"
                        | "osteopath"
                        | "optometrist"
                        | "audiologist"
                    }
                  />
                </CollapsibleSection>

                <CollapsibleSection
                  icon={<Stethoscope className="size-4" />}
                  title={<Trans>Specialty</Trans>}
                  open={openSection === "specialty"}
                  onToggle={() => toggleSection("specialty")}
                >
                  <SpecialtyPicker
                    currentSpecialties={provider.providerSpecialties as PickerSpecialtyEntry[]}
                    specialties={(catalogQuery.data ?? []).map((row) => ({
                      id: row.id,
                      name: row.name,
                      type: row.type,
                      parentId: row.parentSpecialtyId ?? null,
                    }))}
                    locale={locale}
                    onChange={() => {
                      queryClient.invalidateQueries({ queryKey: PROFILE_KEY });
                    }}
                  />
                </CollapsibleSection>

                <CollapsibleSection
                  icon={<FileText className="size-4" />}
                  title={<Trans>Biography</Trans>}
                  open={openSection === "bio"}
                  onToggle={() => toggleSection("bio")}
                >
                  <ProviderBioEditor
                    providerId={provider.id}
                    countryCode={provider.countryCode}
                    initialBio={provider.bio}
                    primaryLang={locale}
                    availableLanguages={availableLanguages}
                    invalidateKey={PROFILE_KEY}
                  />
                </CollapsibleSection>

                <CollapsibleSection
                  icon={<GraduationCap className="size-4" />}
                  title={<Trans>Professional background</Trans>}
                  open={openSection === "credentials"}
                  onToggle={() => toggleSection("credentials")}
                >
                  <CredentialsEditor />
                </CollapsibleSection>

                <CollapsibleSection
                  icon={<Globe className="size-4" />}
                  title={<Trans>Languages spoken</Trans>}
                  open={openSection === "languages"}
                  onToggle={() => toggleSection("languages")}
                >
                  <ProviderLanguagesEditor initial={provider.languages} />
                </CollapsibleSection>
              </div>
            );
          }

          if (id === "locations") {
            const linkedFacilities: OtherFacility[] = activeFacilities.map((fp) => ({
              facilityId: fp.facility.id,
              facilityName: tLocalized(fp.facility.name, locale),
            }));
            return (
              <div className="space-y-3">
                <CollapsibleSection
                  icon={<CalendarIcon className="size-4" />}
                  title={<Trans>Accepted consultation types</Trans>}
                  open={openSection === "consultationTypes"}
                  onToggle={() => toggleSection("consultationTypes")}
                >
                  <ProviderConsultationModesEditor
                    initial={{
                      acceptsInOffice: provider.acceptsInOffice,
                      offersVideoConsultation: provider.offersVideoConsultation,
                      acceptsHomeVisits: provider.acceptsHomeVisits,
                      acceptsPhoneConsultation: provider.acceptsPhoneConsultation,
                      acceptsNewPatients: provider.acceptsNewPatients,
                    }}
                  />
                </CollapsibleSection>

                {provider.acceptsInOffice && (
                  <CollapsibleSection
                    icon={<Building2 className="size-4" />}
                    title={<Trans>Locations setup</Trans>}
                    open={openSection === "facilitySettings"}
                    onToggle={() => toggleSection("facilitySettings")}
                  >
                    <div className="space-y-4">
                      {activeFacilities.length === 0 && pendingFacilities.length === 0 ? (
                        <p className="text-muted-foreground py-4 text-center text-sm italic">
                          <Trans>No medical facilities linked</Trans>
                        </p>
                      ) : (
                        <div className="space-y-4">
                          {activeFacilities.map((fp, idx) => (
                            <FacilitySettingsCard
                              key={fp.id}
                              facility={fp}
                              locale={locale}
                              otherFacilitys={linkedFacilities.filter(
                                (o) => o.facilityId !== fp.facility.id,
                              )}
                              isFirst={idx === 0}
                              onCopied={() => {
                                queryClient.invalidateQueries({ queryKey: PROFILE_KEY });
                              }}
                              onDelete={() => {
                                setConfirmRemove({
                                  facilityId: fp.facilityId,
                                  name: tLocalized(fp.facility.name, locale),
                                });
                              }}
                            />
                          ))}
                          {/* Pending rows: cancel (X) fires directly — same
                              no-confirm behavior as the affiliations page. */}
                          {pendingFacilities.map((fp) => (
                            <FacilitySettingsCard
                              key={fp.id}
                              facility={fp}
                              locale={locale}
                              otherFacilitys={[]}
                              isFirst={false}
                              onDelete={() => void removeFacility(fp.facilityId)}
                            />
                          ))}
                        </div>
                      )}

                      {/* Add facility / personal office (legacy
                          provider-profile-editor.tsx:1861-1873). */}
                      <FacilityLinker
                        providerName={fullName}
                        defaultCountry={provider.countryCode || "RO"}
                        onChanged={() => {
                          queryClient.invalidateQueries({ queryKey: PROFILE_KEY });
                        }}
                      />
                    </div>
                  </CollapsibleSection>
                )}

                {provider.offersVideoConsultation && (
                  <CollapsibleSection
                    icon={<Video className="size-4" />}
                    title={<Trans>Video consultations setup</Trans>}
                    open={openSection === "video"}
                    onToggle={() => toggleSection("video")}
                  >
                    <VideoConsultationEditor
                      videoEnabledInCountry={
                        provider.countryCode === "RO" || provider.countryCode === "BG"
                      }
                      countryName={regionName(provider.countryCode, locale)}
                    />
                  </CollapsibleSection>
                )}

                {provider.acceptsHomeVisits && (
                  <CollapsibleSection
                    icon={<Home className="size-4" />}
                    title={<Trans>Home visit service areas setup</Trans>}
                    open={openSection === "serviceAreas"}
                    onToggle={() => toggleSection("serviceAreas")}
                  >
                    <ServiceAreasEditor countryCode={provider.countryCode} />
                  </CollapsibleSection>
                )}
              </div>
            );
          }

          if (id === "services") {
            const availabilityFacilities: AvailabilityFacilityOption[] = provider.facilityProviders
              .filter((fp) => fp.status === "active")
              .map((fp) => ({ facilityId: fp.facility.id, name: fp.facility.name }));
            const availabilityAreas: AvailabilityAreaOption[] = (
              serviceAreasQuery.data?.areas ?? []
            ).map((a) => ({ id: a.id, name: a.name, cityName: a.cityName }));
            return (
              <ServicesTariffsEditor
                locale={locale}
                countryCode={provider.countryCode}
                initialServices={serviceRows}
                defaultPaymentMethods={provider.paymentMethods ?? []}
                defaultRequiresPrepayment={provider.requiresPrepayment ?? false}
                providerInsuranceIds={(provider.insuranceProviders ?? []).map(
                  (ip) => ip.insuranceId,
                )}
                availabilityFacilities={availabilityFacilities}
                availabilityAreas={availabilityAreas}
              />
            );
          }

          if (id === "calendar") {
            // Legacy tab "Sloturi programări" = slot/schedule editor (NOT the
            // booked-appointments calendar view). 1:1 with legacy line 1792.
            return <UnifiedCalendarTab locale={locale} />;
          }

          if (id === "verification") {
            // Legacy renders only the VerificationDocuments widget on this tab
            // (no CollapsibleSection wrapper). Credentials live on the profile
            // tab — see provider-profile-editor.tsx line 1581.
            return (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="text-primary size-4" />
                  <h2 className="text-foreground flex-1 text-sm font-semibold">
                    <Trans>Identity verification</Trans>
                  </h2>
                  {verificationCompletion > 0 && (
                    <span
                      className={cn(
                        "rounded-full px-2 py-0.5 text-[0.625rem] font-semibold",
                        verificationCompletion === 100
                          ? "bg-success-soft text-success-strong"
                          : "bg-secondary text-secondary-foreground",
                      )}
                    >
                      {verificationCompletion}%
                    </span>
                  )}
                </div>
                <VerificationDocuments onCompletionChange={setVerificationCompletion} />
              </div>
            );
          }

          return null;
        }}
      />

      {/* Unlink-facility confirm — legacy provider-profile-editor used
          window.confirm; ResponsiveSheet is the mandated primitive here. */}
      <ResponsiveSheet
        open={!!confirmRemove}
        onClose={() => setConfirmRemove(null)}
        title={<Trans>Remove location?</Trans>}
        ariaLabel={t`Remove location?`}
      >
        <div className="space-y-4">
          {confirmRemove && (
            <p className="text-foreground text-sm font-medium">{confirmRemove.name}</p>
          )}
          <p className="text-muted-foreground text-sm">
            <Trans>
              All settings, services, and schedule for this location will be permanently deleted.
            </Trans>
          </p>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              size="touch"
              variant="outline"
              disabled={removing}
              onClick={() => setConfirmRemove(null)}
            >
              <Trans>Cancel</Trans>
            </Button>
            <Button
              size="touch"
              variant="destructive"
              disabled={removing}
              onClick={async () => {
                if (!confirmRemove) return;
                await removeFacility(confirmRemove.facilityId);
                setConfirmRemove(null);
              }}
            >
              {removing ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Trash2 className="size-3.5" />
              )}
              <Trans>Remove</Trans>
            </Button>
          </div>
        </div>
      </ResponsiveSheet>

      <AccountSecurityBanner />
    </div>
  );
}

function ProfileStatusPill({ status }: { status: string }) {
  const tone =
    status === "active"
      ? "bg-success-soft text-success-strong"
      : status === "pending_review"
        ? "bg-warning-soft text-warning-strong"
        : status === "draft"
          ? "bg-muted text-muted-foreground"
          : "bg-destructive-soft text-destructive-strong";
  return (
    <span className={`rounded-full px-2 py-0.5 text-[0.625rem] font-semibold ${tone}`}>
      {status === "active" ? (
        <Trans>Live</Trans>
      ) : status === "pending_review" ? (
        <Trans>Pending review</Trans>
      ) : status === "draft" ? (
        <Trans>Draft</Trans>
      ) : (
        status
      )}
    </span>
  );
}

function ProfileSkeleton() {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <Skeleton className="size-20 rounded-full" />
        <div className="space-y-2">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
      <div className="mt-6 flex gap-2 overflow-hidden">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-9 w-24 shrink-0 rounded-lg" />
        ))}
      </div>
      <Skeleton className="h-32 w-full rounded-xl" />
      <Skeleton className="h-48 w-full rounded-xl" />
      <Skeleton className="h-40 w-full rounded-xl" />
    </div>
  );
}

function ProfileErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <LoadErrorState
      className="mt-2"
      title={<Trans>Could not load your provider profile.</Trans>}
      description={<Trans>You may need to complete provider registration first.</Trans>}
      onRetry={onRetry}
    />
  );
}
